import React, {useEffect} from "react"
import styled from "styled-components"
import {useContentCardActions} from "@irati/braze"
import type {Banner as IBanner, Popup as IPopup} from "@irati/braze"
import Flex from "../../Flex"
import Button from "../../Button/Button"
import {ButtonLink, ButtonRouterLink} from "../../Link"
import {Body, H2} from "../../Typography"
import {minWidthMedia} from "../../global/media"
import {pathToRoute} from "./pathToRoute"

const Fill = styled.div<{c1?: string; c2?: string}>`
  display: flex;
  justify-content: left;
  gap: ${({theme}) => theme.spacing.two};
  padding: ${({theme: {spacing}}) => `0 ${spacing.two}`};
  background-image: ${({c1, c2}) => `linear-gradient(to right, ${c1}, ${c2})`};

  & p,
  h2,
  h3 {
    color: white;
  }

  ${({theme}) => theme.mediaQueries.maxWidthMedia.phoneLandscape`
    & img {
      max-height: 100px;
    }
    flex-direction: column;
    align-items: center;
    text-align: center;
  `}

  & img {
    max-height: 150px;
  }

  flex-direction: row-reverse;

  ${minWidthMedia.tablet`
    padding-right: unset;
    padding-left: ${({theme: {spacing}}) => spacing.three}
  `}
`

const Content = styled(Flex)`
  padding: ${({theme: {spacing}}) => `${spacing.two} 0`};

  ${minWidthMedia.phoneLandscape`
    align-items: flex-start;
  `}
`

const Img = styled.img`
  object-fit: contain;
  width: 100%;
  height: 100%;
  max-height: 155px;
`

export function Banner({banner, popup, onClick}: {banner: IBanner; popup?: IPopup; onClick: () => void}) {
  const {app, to} = pathToRoute(banner.ctaUrl)
  const {markContentCardsImpression} = useContentCardActions()

  useEffect(() => {
    if (banner.hideIf === "viewed") {
      markContentCardsImpression([banner.id])
    }
  }, [])

  const buttonProps = {
    small: true,
    inverted: true,
    primary: true,
    full: true,
    onClick,
    children: banner?.ctaCopy
  }

  return (
    <Fill c1={banner.backgroundColor1} c2={banner.backgroundColor2}>
      <Flex flex="1 1 0">
        <Img src={banner.icon} />
      </Flex>

      <Content direction="column" flex="2 0" gap={2} justifyContent="space-between">
        <div>
          <H2 marginBottom={0.5}>{banner.title}</H2>
          <Body>{banner.subtitle}</Body>
        </div>

        <div>
          {popup ? (
            <Button {...buttonProps} />
          ) : banner.ctaUrlExternal || banner.ctaUrl?.startsWith("https") ? (
            <ButtonLink {...buttonProps} href={banner.ctaUrl} target={banner.ctaUrlExternal ? "_blank" : ""} />
          ) : (
            <ButtonRouterLink {...buttonProps} to={to} app={app} />
          )}
        </div>
      </Content>
    </Fill>
  )
}
